import {
    Autocomplete,
    Box,
    Container,
    Divider,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCart, getShoppingLists, setCustomer } from '../../api/user';
import {
    setCart,
    setCartId,
    setCartObject,
    setCartSubtotal,
    setCartTaxes,
    setCartTotal,
    setDiscount,
} from '../../state/cartSlice';
import { setShoppingLists } from '../../state/shoppingListsSlice';
import { setSelectedClient } from '../../state/userSlice';
import { CUSTOM_THEME_COLORS, FONT_STYLE } from '../../variables';


const decodeHtmlEntities = (str) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
};

const ClientSelector = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        user: { sellerInfo, selectedClient },
    } = useSelector((state) => state);

    const { clients } = sellerInfo || [];
    const cart_id = selectedClient?.cart_id;
    const selectedClientId = selectedClient?.id;

    const [clientId, setClientId] = useState(selectedClient?.erp_id);
    const [messageIsVisible, setMessageIsVisible] = useState(false);

    useEffect(() => {
        if (!selectedClientId || selectedClientId === clientId) return;

        setCustomer({ customerId: selectedClientId })
            .then(() => {
                setClientId(selectedClientId);

                return getCart(cart_id).then(
                    ({
                         _id: id,
                         cart_items: cart,
                         taxes,
                         subtotal,
                         total,
                         total_discount,
                         delayedPayments,
                     }) => {
                        dispatch(setCart(cart));
                        dispatch(setCartId(id));
                        dispatch(setDiscount(total_discount));
                        dispatch(setCartTaxes(taxes));
                        dispatch(setCartSubtotal(subtotal));
                        dispatch(setCartTotal(total));
                        dispatch(setCartObject({ delayedPayments }));

                        return getShoppingLists()
                            .then((lists) => {
                                dispatch(setShoppingLists(lists));
                                navigate('/');
                            })
                            .catch(({ response: { status } }) => {
                                if (status === 401) navigate('/login');
                            });
                    }
                )
                    .catch(({ response: { status } }) => {
                        if (status === 401) navigate('/login');
                    });
            })
            .catch(({ response: { status } }) => {
                if (status === 422) setMessageIsVisible(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient, cart_id, dispatch]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    backgroundColor: CUSTOM_THEME_COLORS.lightGray,
                    paddingY: 1.5,
                }}
            >
                <Container>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: 0.5, md: 2 },
                            alignItems: 'center',
                            justifyContent: 'end',
                            flexDirection: { xs: 'column', md: 'row' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                color: CUSTOM_THEME_COLORS.darkGray,
                            }}
                        >
                            <Typography variant='small'>O seu perfil:</Typography>
                            <Typography variant='small' sx={{ fontWeight: FONT_STYLE.bold }}>
                                Vendedor
                            </Typography>
                        </Box>

                        {!!clients.length && (
                            <>
                                <Divider
                                    orientation='vertical'
                                    flexItem
                                    sx={{ borderColor: CUSTOM_THEME_COLORS.darkGray }}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        color: CUSTOM_THEME_COLORS.darkGray,
                                    }}
                                >
                                    <Typography variant='small'>O seu cliente:</Typography>

                                    <Autocomplete
                                        id="client-selection"
                                        size="small"
                                        disableClearable
                                        options={clients}
                                        value={selectedClient || null}
                                        onChange={(_, newValue) => {
                                            dispatch(setSelectedClient(newValue?.companyInfo?.name ?? ''));
                                        }}
                                        getOptionLabel={(option) =>
                                            typeof option === 'string'
                                                ? option
                                                : decodeHtmlEntities(option?.companyInfo?.name ?? '')
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option?.erp_id === value?.erp_id
                                        }
                                        componentsProps={{
                                            popper: {
                                                sx: {
                                                    zIndex: 99999,
                                                },
                                            },
                                            paper: {
                                                sx: {
                                                    maxHeight: 320,
                                                    overflow: 'hidden',
                                                    width: 250,
                                                },
                                            },
                                        }}
                                        ListboxProps={{
                                            sx: {
                                                maxHeight: 300,
                                                overflowY: 'auto',
                                                padding: 0,

                                                '& .MuiAutocomplete-option': {
                                                    fontSize: '12px',
                                                    lineHeight: '18px',
                                                    minHeight: 'auto',
                                                    height: 'auto',
                                                    padding: '10px 12px',
                                                    whiteSpace: 'normal',
                                                    overflow: 'visible',
                                                    textOverflow: 'unset',
                                                    wordBreak: 'break-word',
                                                    alignItems: 'flex-start',

                                                    border: 'none',
                                                    textDecoration: 'none',
                                                    backgroundImage: 'none',

                                                    '&::before': {
                                                        display: 'none',
                                                        content: 'none',
                                                    },

                                                    '&::after': {
                                                        display: 'none',
                                                        content: 'none',
                                                    },
                                                },
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} sx={{ marginTop: 0 }} />
                                        )}
                                        sx={{
                                            width: 250,
                                            '.MuiInputBase-root': { fontSize: '0.9em' },
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>
                </Container>
            </Box>

            <Snackbar
                autoHideDuration={6000}
                message='Houve um problema na alteração do cliente'
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={messageIsVisible}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;
                    setMessageIsVisible(false);
                }}
                ContentProps={{
                    sx: { backgroundColor: CUSTOM_THEME_COLORS.red },
                }}
            />
        </>
    );
};

export default ClientSelector;